import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ClientSearchResult } from "../types/client";
import type { ClientSearchSource } from "./clientSearchSource";
import { mergeClientRows } from "./mergeClientRows";

export type PolicyLegState = "idle" | "loading" | "ok" | "failed" | "unavailable";

export interface ClientSearchOptions {
  /** Shortest trimmed query that runs anything (the header's today: 3). */
  minChars?: number;
  /** Quiet time after the last keystroke before a leg starts (the header's today: 300). */
  debounceMs?: number;
}

export interface ClientSearchState {
  /** Policy-number rows first, then name rows, one per client. */
  rows: ClientSearchResult[];
  /** At least one leg that runs for this query has not answered yet. */
  loading: boolean;
  /** The name leg failed: show the failure with Retry, never "No clients found.". */
  nameFailed: boolean;
  policyLeg: PolicyLegState;
  /**
   * Every leg that runs for this query has answered (ok or failed). False when
   * nothing runs (query too short). "No clients found." may show only when
   * `settled && !nameFailed && policyLeg !== "failed" && rows.length === 0`.
   */
  settled: boolean;
  /** Re-runs only the leg(s) that failed, without the debounce. */
  retry(): void;
}

export const DEFAULT_MIN_CHARS = 3;
export const DEFAULT_DEBOUNCE_MS = 300;
/** The vault's policy-search gate (SearchVaultGroupQueryHandler.MinQueryLength) plus a digit. */
const POLICY_MIN_CHARS = 3;
const HAS_DIGIT = /\d/;

type LegStatus = "ok" | "failed" | "unavailable";
/** A leg's answer, tagged with the query it answers so a stale one is never read. */
interface LegResult {
  query: string;
  status: LegStatus;
  rows: ClientSearchResult[];
}

/**
 * The client picker's search: a name leg and a policy-number leg run
 * independently (the slow policy leg never holds back name rows), each behind
 * its own debounce and stale-answer guard. A failed leg is a failure state,
 * never an empty list.
 *
 * `source` must be referentially stable (memoise it): a new identity restarts
 * both legs.
 */
export function useClientSearch(
  source: ClientSearchSource,
  query: string,
  opts: ClientSearchOptions = {}
): ClientSearchState {
  const minChars = opts.minChars ?? DEFAULT_MIN_CHARS;
  const debounceMs = opts.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const trimmed = query.trim();

  const nameEligible = trimmed.length >= minChars;
  const policyEligible =
    nameEligible &&
    source.searchByPolicy !== undefined &&
    trimmed.length >= POLICY_MIN_CHARS &&
    HAS_DIGIT.test(trimmed);

  const [nameResult, setNameResult] = useState<LegResult | null>(null);
  const [policyResult, setPolicyResult] = useState<LegResult | null>(null);
  const [nameTick, setNameTick] = useState(0);
  const [policyTick, setPolicyTick] = useState(0);

  // Set by retry() so the next run of that leg skips the debounce.
  const nameImmediate = useRef(false);
  const policyImmediate = useRef(false);

  useEffect(() => {
    if (!nameEligible) return undefined;
    // Each run owns a `live` flag its cleanup clears: a superseded or unmounted run's answer is dropped.
    let live = true;
    const delay = nameImmediate.current ? 0 : debounceMs;
    nameImmediate.current = false;
    const timer = setTimeout(() => {
      source.searchByName(trimmed).then(
        (rows) => {
          if (live) setNameResult({ query: trimmed, status: "ok", rows });
        },
        () => {
          if (live) setNameResult({ query: trimmed, status: "failed", rows: [] });
        }
      );
    }, delay);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [source, trimmed, nameEligible, debounceMs, nameTick]);

  useEffect(() => {
    const searchByPolicy = source.searchByPolicy?.bind(source);
    if (!policyEligible || !searchByPolicy) return undefined;
    let live = true;
    const delay = policyImmediate.current ? 0 : debounceMs;
    policyImmediate.current = false;
    const timer = setTimeout(() => {
      searchByPolicy(trimmed).then(
        (outcome) => {
          if (!live) return;
          setPolicyResult(
            outcome.status === "ok"
              ? { query: trimmed, status: "ok", rows: outcome.items }
              : { query: trimmed, status: outcome.status, rows: [] }
          );
        },
        () => {
          if (live) setPolicyResult({ query: trimmed, status: "failed", rows: [] });
        }
      );
    }, delay);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [source, trimmed, policyEligible, debounceMs, policyTick]);

  const nameAnswer = nameEligible && nameResult?.query === trimmed ? nameResult : null;
  const policyAnswer = policyEligible && policyResult?.query === trimmed ? policyResult : null;

  const nameFailed = nameAnswer?.status === "failed";
  const policyLeg: PolicyLegState = !policyEligible ? "idle" : (policyAnswer?.status ?? "loading");

  const rows = useMemo(
    () =>
      mergeClientRows(
        nameAnswer?.status === "ok" ? nameAnswer.rows : [],
        policyAnswer?.status === "ok" ? policyAnswer.rows : []
      ),
    [nameAnswer, policyAnswer]
  );

  const loading = nameEligible && (nameAnswer === null || policyLeg === "loading");
  const settled = nameEligible && nameAnswer !== null && policyLeg !== "loading";

  // Reads the answers at call time through a ref so retry stays referentially stable.
  const failedLegs = useRef({ name: false, policy: false });
  failedLegs.current = { name: nameFailed, policy: policyLeg === "failed" };
  const retry = useCallback(() => {
    if (failedLegs.current.name) {
      nameImmediate.current = true;
      setNameResult(null);
      setNameTick((t) => t + 1);
    }
    if (failedLegs.current.policy) {
      policyImmediate.current = true;
      setPolicyResult(null);
      setPolicyTick((t) => t + 1);
    }
  }, []);

  return { rows, loading, nameFailed, policyLeg, settled, retry };
}
